import moment from 'moment';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import PublicChatbotConversationResource from 'App/resources/PublicChatbotConversation';
import type PublicChatbotConversation from 'App/resources/Model/PublicChatbotConversation';

type DateLike = Date | string | null;

/** The timeline entry type a website chat rides in as, on the Angular cards. */
export const WEBSITE_CHAT_ENTITY = 'WebsiteChat';

export type WebsiteChatEvent = {
	id: string;
	entityType: typeof WEBSITE_CHAT_ENTITY;
	date: string;
	chat: PublicChatbotConversation;
};

/**
 * The handed-over website chats of one company or contact, as timeline entries
 * (WEB-12989). Only chats that became a to-do or a ticket (owner decision); the
 * API checks each row against the one read rule. Empty without the chatbot.
 */
export const loadWebsiteChatEvents = async (link: {
	clientId?: number;
	contactId?: number;
}): Promise<WebsiteChatEvent[]> => {
	if (!hasSoftDeployAccess('AI_PUBLIC_CHATBOT') || (!link.clientId && !link.contactId)) return [];
	const chats = await PublicChatbotConversationResource.getLinked(
		link.contactId ? { contactId: link.contactId } : { clientId: link.clientId }
	);
	return chats.map(chat => ({ id: `chat-${chat.id}`, entityType: WEBSITE_CHAT_ENTITY, date: chat.regDate, chat }));
};

/**
 * The chats that may join a timeline whose events are paged newest first. While
 * older events are still unloaded, only chats inside the loaded range join — the
 * rest wait for "Load more", so they never bunch up above it out of order.
 */
export const visibleChats = <C extends { date: DateLike }>(
	events: Array<{ date?: DateLike }>,
	chats: C[],
	hasMore: boolean
): C[] => {
	if (!hasMore) return chats;
	const oldest = events.length ? events[events.length - 1].date : null;
	return oldest ? chats.filter(chat => moment(chat.date).isSameOrAfter(oldest)) : [];
};

/** Events and the chats that may join them, newest first — the order the timeline draws. */
export const withWebsiteChats = <E extends { date?: DateLike }, C extends { date: DateLike }>(
	events: E[],
	chats: C[],
	hasMore: boolean
): Array<E | C> =>
	[...events, ...visibleChats(events, chats, hasMore)].sort(
		(a, b) => moment(b.date ?? 0).valueOf() - moment(a.date ?? 0).valueOf()
	);

type DashboardScope = {
	events: Array<{ date?: DateLike }>;
	eventsLength: number;
	totalEvents: number;
	historyType: string | null;
	eventSearch: string;
	timeline?: Array<{ date?: DateLike }>;
	$watchCollection: (expression: string, listener: () => void) => void;
	$watchGroup: (expressions: string[], listener: () => void) => void;
};

/**
 * The Angular account and contact dashboards (the cards every customer has
 * while REACT_CLIENT_CARD / REACT_CONTACT_CARD stay unreleased): keeps
 * `scope.timeline` as `scope.events` plus the handed-over chats, which the
 * template repeats instead of `events`. The chats sit out a focused type and a
 * search — they are neither.
 */
export const bindWebsiteChatTimeline = (
	scope: DashboardScope,
	link: { clientId?: number; contactId?: number },
	apply: () => void
) => {
	let chats: WebsiteChatEvent[] = [];
	const refresh = () => {
		const show = (scope.historyType === null || scope.historyType === 'multi') && !scope.eventSearch;
		scope.timeline = withWebsiteChats(
			scope.events ?? [],
			show ? chats : [],
			scope.eventsLength < scope.totalEvents
		);
	};
	scope.timeline = scope.events;
	scope.$watchCollection('events', refresh);
	scope.$watchGroup(['historyType', 'eventSearch', 'eventsLength', 'totalEvents'], refresh);
	return loadWebsiteChatEvents(link).then(rows => {
		chats = rows;
		refresh();
		apply();
	});
};
